/* eslint-disable @typescript-eslint/no-explicit-any */
import i18n from "@/features/utils/i18n";
import * as Sentry from "@sentry/react";
import React from "react";
import { createRoot } from "react-dom/client";
import "./global.css";
import { initAnalytics } from "./features/analytics/posthog";
import { ReactQueryProvider } from "./lib/react-query.tsx";
import App from "./router.tsx";

if (!document.location.origin.includes("localhost")) {
  Sentry.init({
    dsn: "https://f8d7939f3ae4929718ee4b5543326358@o4510788672749568.ingest.de.sentry.io/4510788682186832",
    sendDefaultPii: true,
    environment: document.location.origin.includes("dev")
      ? "development"
      : "production",
  });
}

initAnalytics();

class ErrorBoundary extends React.Component<{
  children: React.ReactNode;
  fallback: React.ReactNode;
}> {
  state: { hasError: boolean };

  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    Sentry.captureException(error, { extra: errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return <h1>{this.props.fallback}</h1>;
    }

    return this.props.children;
  }
}

// Whatever crashed may be the app's own rendering, translations included: the
// English sentence is what shows when the locale file never loaded.
function CrashMessage() {
  return (
    <>
      {i18n.t("common.crash", {
        defaultValue: "An error has occurred, please reload the page.",
      })}
    </>
  );
}

createRoot(document.getElementById("root")!).render(
  <ReactQueryProvider>
    <ErrorBoundary fallback={<CrashMessage />}>
      <App />
    </ErrorBoundary>
  </ReactQueryProvider>,
);
